/**
 * Blog API service — public + admin endpoints for the Knowledge Hub.
 * Uses fetch directly; auth token (if present) is sent as Bearer.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE = `${API_URL}/api/blog`;

function getToken() {
  return localStorage.getItem('sip_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (!options.isFormData) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    let detail = 'Request failed';
    try {
      const j = await res.json();
      detail = typeof j.detail === 'string' ? j.detail : (j.detail?.message || detail);
    } catch (_e) { /* noop */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export const blogApi = {
  // Public
  listPosts: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    return request(`/posts?${q.toString()}`);
  },
  getPost: (slug) => request(`/posts/${slug}`),
  getRelated: (slug, limit = 4) => request(`/posts/${slug}/related?limit=${limit}`),
  trending: (limit = 6) => request(`/trending?limit=${limit}`),
  listCategories: () => request('/categories'),
  listTags: () => request('/tags'),

  // Auth
  bookmark: (postId) => request(`/posts/${postId}/bookmark`, { method: 'POST' }),
  unbookmark: (postId) => request(`/posts/${postId}/bookmark`, { method: 'DELETE' }),
  myBookmarks: () => request('/me/bookmarks'),
  rate: (postId, helpful) =>
    request(`/posts/${postId}/rating`, { method: 'POST', body: JSON.stringify({ helpful }) }),
  getRating: (postId) => request(`/posts/${postId}/rating`),

  // Comments
  listComments: (postId) => request(`/posts/${postId}/comments`),
  createComment: (postId, body, parentId = null) =>
    request(`/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body, parentId }),
    }),
  deleteComment: (commentId) => request(`/comments/${commentId}`, { method: 'DELETE' }),

  // Newsletter
  newsletter: (payload) =>
    request('/newsletter', { method: 'POST', body: JSON.stringify(payload) }),

  // Super Admin
  admin: {
    listPosts: (status = '') => request(`/admin/posts${status ? `?status=${status}` : ''}`),
    getPost: (postId) => request(`/admin/posts/${postId}`),
    createPost: (payload) =>
      request('/admin/posts', { method: 'POST', body: JSON.stringify(payload) }),
    updatePost: (postId, payload) =>
      request(`/admin/posts/${postId}`, { method: 'PUT', body: JSON.stringify(payload) }),
    deletePost: (postId) => request(`/admin/posts/${postId}`, { method: 'DELETE' }),
    createCategory: (payload) =>
      request('/admin/categories', { method: 'POST', body: JSON.stringify(payload) }),
    moderationQueue: (status = 'pending') =>
      request(`/admin/moderation/queue?status=${status}`),
    approveComment: (commentId) =>
      request(`/admin/moderation/${commentId}/approve`, { method: 'POST' }),
    rejectComment: (commentId) =>
      request(`/admin/moderation/${commentId}/reject`, { method: 'POST' }),
    seed: () => request('/admin/seed', { method: 'POST' }),
  },
};

export default blogApi;
